'use client';

import { useCallback, useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import { cn } from '@/shadcn/lib/utils';
import { Listing } from '../types';
import MapboxMap from '@/shared/components/maps/MapboxMap';
import { formatPrice, mapCurrencyCodeToText, formatPriceToShort } from '@/features/adverts/utils';

const SOURCE_ID = 'listings-clusters-source';
const CLUSTERS_LAYER_ID = 'listings-clusters-layer';
const CLUSTER_COUNT_LAYER_ID = 'listings-clusters-count-layer';
const UNCLUSTERED_LAYER_ID = 'listings-unclustered-layer';
const POINTS_FROM_ZOOM = 12;

type Props = {
    className?: string | string[];
    height?: string;
    center?: [number, number];
    zoom?: number;
    listings?: Listing[];
    showLoadMore?: boolean;
    activeListingId?: string | null;
    /** detail v split view: mapa zletí na ponuku, dostane pin, ostatne cenovky stlmi; null = spat na povodny vyrez */
    focusListingId?: string | null;
    onLoadMore?: () => void;
};

const FOCUS_ZOOM = 15;

export function ListingsMap(props: Props) {
    const mapRef = useRef<mapboxgl.Map | null>(null);
    const markersRef = useRef<mapboxgl.Marker[]>([]);
    const activeDetailPopupRef = useRef<mapboxgl.Popup | null>(null);
    const isFirstListingsRender = useRef(true);

    // Refs so event handlers always read the latest values without stale closures
    const listingsRef = useRef(props.listings);
    const activeListingIdRef = useRef(props.activeListingId);
    const focusListingIdRef = useRef(props.focusListingId);
    const focusPinRef = useRef<mapboxgl.Marker | null>(null);
    // kamera pred fokusom - pri zatvoreni detailu sa mapa vrati presne tam, kde bola
    const savedCameraRef = useRef<{ center: mapboxgl.LngLat; zoom: number } | null>(null);
    useEffect(() => {
        listingsRef.current = props.listings;
    }, [props.listings]);
    useEffect(() => {
        activeListingIdRef.current = props.activeListingId;
    }, [props.activeListingId]);

    const clearActiveDetailPopup = useCallback(() => {
        activeDetailPopupRef.current?.remove();
        activeDetailPopupRef.current = null;
    }, []);

    const clearMarkers = useCallback(() => {
        markersRef.current.forEach((m) => m.remove());
        markersRef.current = [];
        clearActiveDetailPopup();
    }, [clearActiveDetailPopup]);

    const toggleDetailMarker = useCallback(
        (listing: Listing) => {
            const map = mapRef.current;
            if (!map) return;

            const current = activeDetailPopupRef.current as
                | (mapboxgl.Popup & { __listingId?: string })
                | null;

            if (current?.__listingId === listing.id) {
                clearActiveDetailPopup();
                return;
            }

            clearActiveDetailPopup();

            const detailEl = document.createElement('div');
            detailEl.style.cssText =
                'min-width:210px;max-width:240px;border-radius:12px;overflow:hidden;background:#fff;box-shadow:0 4px 20px rgba(0,0,0,0.15);font-family:inherit;';

            const priceText = listing.priceOnRequest
                ? 'Cena dohodou'
                : `${formatPrice(listing?.finalPrice ?? 0)} ${mapCurrencyCodeToText(listing.currencyCode)}`;

            const locationParts = [listing.municipality?.name, listing.district?.name].filter(
                Boolean,
            );
            const locationText = locationParts.join(', ');

            const imgHtml = `<div style="width:100%;height:100%;background:#e5e7eb;display:flex;align-items:center;justify-content:center;">
                <svg width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="#9ca3af" stroke-width="1.5">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3 9.75h18M3 9.75A6.75 6.75 0 019.75 3h4.5A6.75 6.75 0 0121 9.75v4.5A6.75 6.75 0 0114.25 21H9.75A6.75 6.75 0 013 14.25V9.75z" />
                </svg>
            </div>`;

            detailEl.innerHTML = `
                <div style="width:100%;height:110px;overflow:hidden;flex-shrink:0;">${imgHtml}</div>
                <div style="padding:10px 12px 12px;">
                    <div style="font-size:15px;font-weight:700;color:#111827;line-height:1.2;">${priceText}</div>
                    <div style="font-size:12px;font-weight:500;color:#374151;margin-top:4px;overflow:hidden;white-space:nowrap;text-overflow:ellipsis;">${listing.slogan}</div>
                    ${locationText ? `<div style="font-size:11px;color:#9ca3af;margin-top:2px;overflow:hidden;white-space:nowrap;text-overflow:ellipsis;">${locationText}</div>` : ''}
                    <a href="/inzerat/${listing.id}/${listing.slug}" style="display:block;margin-top:10px;padding:7px 12px;background:#111827;color:#fff;font-size:11px;font-weight:600;text-align:center;border-radius:6px;text-decoration:none;cursor:pointer;">Zobraziť detail</a>
                </div>
            `;

            detailEl.addEventListener('click', (e) => e.stopPropagation());

            const popup = new mapboxgl.Popup({
                closeButton: false,
                closeOnClick: false,
                closeOnMove: false,
                offset: [0, -6],
                anchor: 'bottom',
                className: 'listing-detail-popup',
            })
                .setLngLat([listing.longitude ?? 0, listing.latitude ?? 0])
                .setDOMContent(detailEl)
                .addTo(map);

            (popup as mapboxgl.Popup & { __listingId?: string }).__listingId = listing.id;
            activeDetailPopupRef.current = popup;
        },
        [clearActiveDetailPopup],
    );

    const updateMarkers = useCallback(() => {
        const map = mapRef.current;
        if (!map) return;
        if (!map.isStyleLoaded()) {
            map.once('idle', updateMarkers);
            return;
        }

        clearMarkers();

        if (map.getZoom() < POINTS_FROM_ZOOM) return;

        const listings = listingsRef.current;
        const activeListingId = activeListingIdRef.current;

        const focusId = focusListingIdRef.current;
        const nextMarkers: mapboxgl.Marker[] =
            listings
                // detail: fokusovana ponuka ma pin, jej cenovku nekreslim vobec
                ?.filter((p) => p.id !== focusId)
                .map((p) => {
                    const el = document.createElement('div');
                    const isActive = activeListingId === p.id;
                    el.className = cn(
                        'text-[10px] font-bold text-white whitespace-nowrap rounded-[20px] px-[8px] py-[3px] transition duration-300 [transition-timing-function:cubic-bezier(.22,.61,.36,1)]',
                        isActive ? 'bg-black' : 'bg-cust-primary',
                        // v detaile su ostatne cenovky mensi kontext (opacity ide cez Marker option - Mapbox v3 si ju drzi inline, class by prehral)
                        focusId && 'scale-[0.88]',
                    );
                    el.innerText = formatPriceToShort(p.finalPrice ?? 0);
                    el.addEventListener('click', (e) => {
                        e.stopPropagation();
                        toggleDetailMarker(p);
                    });

                    return new mapboxgl.Marker({ element: el, anchor: 'bottom', opacity: focusId ? '0.5' : '1' })
                        .setLngLat([p.longitude ?? 0, p.latitude ?? 0])
                        .addTo(map);
                }) ?? [];

        markersRef.current = nextMarkers;
    }, [clearMarkers, toggleDetailMarker]);

    /**
     * Fokus na ponuku (detail v split view): ta ista instancia mapy, len kamera + pin + stlmene cenovky.
     * Bez cakania na style - easeTo aj DOM marker funguju hned; isStyleLoaded je pocas resize stlpca
     * a letu casto false a 'load' uz nepride, 'idle' je v tom case nespolahlivy.
     */
    const applyFocus = useCallback(() => {
        const map = mapRef.current;
        if (!map) return;
        const id = focusListingIdRef.current;
        const listing = id ? listingsRef.current?.find((l) => l.id === id) : undefined;
        focusPinRef.current?.remove();
        focusPinRef.current = null;
        clearActiveDetailPopup();

        if (listing && listing.longitude != null && listing.latitude != null) {
            if (!savedCameraRef.current) {
                savedCameraRef.current = { center: map.getCenter(), zoom: map.getZoom() };
            }
            const el = document.createElement('div');
            el.className = 'rx-map-focus-pin';
            el.style.cssText = 'width:46px;height:46px;';
            el.innerHTML = '<img src="/images/map_pin.png" alt="" style="width:100%;height:100%;display:block" draggable="false" />';
            focusPinRef.current = new mapboxgl.Marker({ element: el, anchor: 'center' })
                .setLngLat([listing.longitude, listing.latitude])
                .addTo(map);
            // stlpec mapy sa zaroven zuzuje (transition 600ms, ResizeObserver vola map.resize - ten let neprerusi)
            map.easeTo({
                center: [listing.longitude, listing.latitude],
                zoom: FOCUS_ZOOM,
                duration: 900,
                essential: true,
            });
        } else if (savedCameraRef.current) {
            const cam = savedCameraRef.current;
            savedCameraRef.current = null;
            map.easeTo({ center: cam.center, zoom: cam.zoom, duration: 700, essential: true });
        }
        updateMarkers();
    }, [clearActiveDetailPopup, updateMarkers]);

    const updateSourceData = useCallback(() => {
        const map = mapRef.current;
        if (!map) return;
        const source = map.getSource(SOURCE_ID) as mapboxgl.GeoJSONSource | undefined;
        if (!source) return;

        // po dotiahnuti klastrov z workera vynutit prekreslenie - inak sa
        // vrstvy niekedy ukazu az po prvom pohybe mysou / window resize
        const onSourceData = (e: mapboxgl.MapSourceDataEvent) => {
            if (e.sourceId !== SOURCE_ID || !e.isSourceLoaded) return;
            map.off('sourcedata', onSourceData);
            map.resize();
        };
        map.on('sourcedata', onSourceData);

        source.setData({
            type: 'FeatureCollection',
            features:
                listingsRef.current?.map((i) => ({
                    type: 'Feature' as const,
                    geometry: {
                        type: 'Point' as const,
                        coordinates: [i.longitude ?? 0, i.latitude ?? 0],
                    },
                    properties: { id: i.id },
                })) ?? [],
        });
    }, []);

    const handleMapLoaded = useCallback(
        (map: mapboxgl.Map) => {
            mapRef.current = map;
            map.addSource(SOURCE_ID, {
                type: 'geojson',
                data: { type: 'FeatureCollection', features: [] },
                cluster: true,
                clusterMaxZoom: POINTS_FROM_ZOOM - 1,
                clusterRadius: 50,
            });

            map.addLayer({
                id: CLUSTERS_LAYER_ID,
                type: 'circle',
                source: SOURCE_ID,
                filter: ['has', 'point_count'],
                paint: {
                    'circle-color': '#111827',
                    'circle-radius': ['step', ['get', 'point_count'], 13, 10, 16, 25, 19, 50, 23],
                    'circle-stroke-width': 1.5,
                    'circle-stroke-color': '#ffffff',
                },
                minzoom: 0,
            });

            map.addLayer({
                id: CLUSTER_COUNT_LAYER_ID,
                type: 'symbol',
                source: SOURCE_ID,
                filter: ['has', 'point_count'],
                layout: {
                    'text-field': ['get', 'point_count_abbreviated'],
                    'text-size': 14,
                    'text-font': ['DIN Pro Bold', 'Arial Unicode MS Bold'],
                    'text-letter-spacing': -0.02,
                },
                paint: { 'text-color': '#ffffff' },
                maxzoom: POINTS_FROM_ZOOM,
            });

            // nezhlukovane body pod POINTS_FROM_ZOOM: bez tejto vrstvy su neviditelne
            // (HTML markery sa kreslia az od zoomu 12) - zlaty bod s ciernym okrajom
            map.addLayer({
                id: UNCLUSTERED_LAYER_ID,
                type: 'circle',
                source: SOURCE_ID,
                filter: ['!', ['has', 'point_count']],
                paint: {
                    'circle-color': '#F3C300',
                    'circle-radius': 7,
                    'circle-stroke-width': 2,
                    'circle-stroke-color': '#111827',
                },
                maxzoom: POINTS_FROM_ZOOM,
            });

            map.on('click', UNCLUSTERED_LAYER_ID, (e) => {
                const g = e.features?.[0]?.geometry;
                if (!g || g.type !== 'Point') return;
                map.easeTo({ center: g.coordinates as [number, number], zoom: POINTS_FROM_ZOOM, duration: 500 });
            });
            map.on('mouseenter', UNCLUSTERED_LAYER_ID, () => { map.getCanvas().style.cursor = 'pointer'; });
            map.on('mouseleave', UNCLUSTERED_LAYER_ID, () => { map.getCanvas().style.cursor = ''; });

            map.on('click', CLUSTERS_LAYER_ID, (e) => {
                const feature = e.features?.[0];
                if (!feature) return;

                const clusterId = feature.properties?.cluster_id;
                const source = map.getSource(SOURCE_ID) as mapboxgl.GeoJSONSource & {
                    getClusterExpansionZoom: (
                        clusterId: number,
                        callback: (error: Error | null, zoom: number) => void,
                    ) => void;
                };

                source.getClusterExpansionZoom(clusterId, (err, targetZoom) => {
                    if (err) return;
                    const coordinates = (feature.geometry as GeoJSON.Point).coordinates as [
                        number,
                        number,
                    ];
                    map.easeTo({ center: coordinates, zoom: targetZoom as number, duration: 500 });
                });
            });

            map.on('mouseenter', CLUSTERS_LAYER_ID, () => {
                map.getCanvas().style.cursor = 'pointer';
            });
            map.on('mouseleave', CLUSTERS_LAYER_ID, () => {
                map.getCanvas().style.cursor = '';
            });

            updateSourceData();
            updateMarkers();

            map.on('moveend', updateMarkers);
            map.on('zoomend', updateMarkers);

            // detail otvoreny skor, nez mapa nabehla (lazy mount) - fokus dobehne teraz
            if (focusListingIdRef.current) applyFocus();
        },
        [updateSourceData, updateMarkers, applyFocus],
    );

    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;
        const run = () => {
            updateSourceData();
            updateMarkers();
        };
        if (map.isStyleLoaded()) run();
        else map.once('load', run);
    }, [props.listings, props.activeListingId, updateSourceData, updateMarkers]);

    useEffect(() => {
        if (isFirstListingsRender.current) {
            isFirstListingsRender.current = false;
            return;
        }
        const map = mapRef.current;
        const first = props.listings?.[0];
        // v detaile drzi kameru fokus - zmena zoznamu (chipy) ju nema preberat
        if (!map || !first || focusListingIdRef.current) return;
        const fly = () =>
            map.easeTo({ center: [first.longitude ?? 0, first.latitude ?? 0], duration: 600 });
        if (map.isStyleLoaded()) fly();
        else map.once('load', fly);
    }, [props.listings]);

    useEffect(() => {
        focusListingIdRef.current = props.focusListingId;
        applyFocus();
    }, [props.focusListingId, applyFocus]);

    return (
        <MapboxMap
            height={props.height}
            className={props.className}
            center={props.center}
            zoom={props.zoom}
            onMapLoaded={handleMapLoaded}
        />
    );
}
